import { View, SxProp, useDripsyTheme, SafeAreaView, ScrollView } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useContext, useState } from 'react';
import { H3, H5, TextBody, TextCaption } from '@/design-system/components/typography';
import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { TabKeys } from '../../options';
import { AccountDetails } from './Details.account';
import { CardDetails } from './Details.card';
import { AccountDetails as AccountDetailsNative } from './DetailsNative.account';
import { CardDetails as CardDetailsNative } from './DetailsNative.card';
import { LayoutContext } from 'contexts/LayoutContext';
import { PageContainer } from '../page/PageContainer';

import { AccountDetailsTabParamList, AccountDetailsTabRoutes, AccountDetailsTabScreenProps } from './type';
import { useCard } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { CardAccountType, CardAccountTypeColor } from '@/features/card-account/enum';
import { CardAccountStatus, CardAccountStatusColor } from '@/features/account-management/enum';
import { CardAccountTypeFilters } from '@/features/card-account/options';
import { head, capitalize } from 'lodash';
import { Row } from '@/design-system/components/layout';

import { useCardKycInfo } from '@/features/account-management/screens/details/hooks/useCardKycInfo.hook';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';

export interface AccountDetailsScreenProps {
  account: string;
  tab?: number;
  record: any;
  flag?: string;
  options?: { [key: string]: any };
}

type ColorTypes = keyof typeof CardAccountTypeColor;
type ColorStatusTypes = keyof typeof CardAccountStatusColor;

export const AccountDetailsCardScreen: React.FC<AccountDetailsTabScreenProps<AccountDetailsTabRoutes.Card>> = ({ route }) => {
  const navigation = useNavigation();
  const { t } = useTranslation('account-management');
  const { theme } = useDripsyTheme();
  const { account, tab, record, flag, options } = route.params;
  const layout = useContext(LayoutContext);
  const [tabIndex, setTabIndex] = useState(tab || 0);

  const onBackPressed = () => {
    navigation.getParent()?.goBack();
  };

  const { clientIdentity, isLoading, reload } = useCardKycInfo({
    cardAccountId: account
  });

  const upperFirstCharacter = (value: string) => {
    if (value.length === 0) return value;
    return value[0].toUpperCase() + value.substring(1);
  };

  const { cardAccounts } = useCard({ cardAccountId: record?.cardAccountId, cardId: record?.cardId });
  const cardAccountOverview = head(cardAccounts);

  return layout.isMobile ? (
    <>
      <SafeAreaView sx={styles.containerSaft}>
        <ScrollView sx={styles.scrollContent} nestedScrollEnabled>
          <View sx={{ ...styles.container, margin: 0 }}>
            {tabIndex === 0 ? <AccountDetailsNative account={account} tab={tab} record={record} /> : null}
            {tabIndex === 1 && record?.cardAccountId ? (
              <CardDetailsNative account={account} tab={tab} record={record} flag={flag} options={options} />
            ) : null}
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  ) : (
    <ScrollView sx={styles.containerWeb} nestedScrollEnabled>
      <PageContainer sx={{ paddingX: '$10', paddingY: '$10' }} childrenSx={{ zIndex: 1 }}>
        <View sx={{ ...styles.container }}>
          <View sx={styles.back}>
            <UniIconButton
              sx={styles.backButton}
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              icon="arrowLeft"
              onPress={onBackPressed}
            />
            <TextCaption hyperlink>
              {flag == '1'
                ? t('card-account-management::account-page.text--back-to-card-account')
                : t('card-account-management::account-page.text--back-to-account-management')}
            </TextCaption>
          </View>
          <View sx={styles.header}>
            <View sx={styles.accountInfo}>
              <Icon name="accountProfile" width={60} height={60} />
              <View sx={styles.account}>
                <H3 bold>{(flag == '1' ? record?.label : record?.name) || '-'}</H3>
                <TextBody sx={{ mt: '$1' }}>
                  {(flag == '1' ? (
                    <View sx={{ gap: '$2', flexDirection: 'row' }}>
                      <View
                        sx={{
                          ...styles.tagSx,
                          backgroundColor: theme.colors?.$tagBlackBackground as string
                        }}>
                        <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{record.assetSymbol}</TextCaption>
                      </View>
                      <View
                        sx={{
                          ...styles.tagSx,
                          backgroundColor: theme.colors[`$tag${CardAccountTypeColor[record.type as ColorTypes]}Background`] as string
                        }}>
                        <TextCaption sx={{ color: theme.colors[`$tag${CardAccountTypeColor[record.type as ColorTypes]}Text`] as string }}>
                          {CardAccountTypeFilters(t).find((rec) => rec.value === record.type)?.label}
                        </TextCaption>
                      </View>
                      <View
                        sx={{
                          ...styles.tagSx,
                          backgroundColor: theme.colors[`$tag${CardAccountStatusColor[cardAccountOverview?.status as ColorStatusTypes]}Background`] as string
                        }}>
                        <TextCaption sx={{ color: theme.colors[`$tag${CardAccountStatusColor[cardAccountOverview?.status as ColorStatusTypes]}Text`] as string }}>
                          {capitalize(cardAccountOverview?.status)}
                        </TextCaption>
                      </View>
                    </View>
                  ) : (
                    record?.accountNumber
                  )) || '-'}
                </TextBody>
              </View>
            </View>
            <View sx={styles.kycInfoButtonWrap}>
              <Row sx={{ justifyContent: 'end', flexDirection: 'row', gap: '$3' }}>
                <UniButton
                  sx={{ width: 200, height: 44 }}
                  labelSx={{ color: '$black' }}
                  label={t('card-account-management::card-details.button--update-kyc-info')}
                  disabled={isLoading}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.UpdateKycInfo,
                      
                      params: {
                        data: clientIdentity,
                        refresh: () => {
                          reload();
                        }
                      }
                    });
                  }}
                />
              </Row>
            </View>
          </View>
          {flag != '1' && record?.cardAccountId && (
            <View sx={styles.operations}>
              <SegmentSelect
                data={TabKeys}
                value={tabIndex}
                wrapperBackgroundColor={theme?.colors?.$backgroundPrimary as string}
                buttonWidth={188}
                buttonBackgroundColors={theme?.linearGradients?.SegmentSelectActive}
                labelColorActive={theme?.colors?.$white as string}
                labelColorInactive={theme?.colors?.$navbarTextInactive as string}
                onValueChange={setTabIndex}
              />
            </View>
          )}
          <View sx={styles.contentContainer}>
            {tabIndex === 0 ? <AccountDetails account={record?.id} tab={tab} record={record} /> : null}
            {tabIndex === 1 && record?.cardAccountId ? (
              <CardDetails account={record?.cardAccountId} tab={tab} record={record} flag={flag} options={options} />
            ) : null}
          </View>
        </View>
      </PageContainer>
    </ScrollView>
  );
};

enum StyleName {
  containerSaft = 'containerSaft',
  scrollContent = 'scrollContent',
  container = 'container',
  back = 'back',
  backButton = 'backButton',
  header = 'header',
  accountInfo = 'accountInfo',
  kycInfoButtonWrap = 'kycInfoButtonWrap',
  account = 'account',
  operations = 'operations',
  contentContainer = 'contentContainer',
  containerWeb = 'containerWeb',
  tagSx = 'tagSx'
}

const styles: Record<StyleName, SxProp> = {
  containerSaft: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary'
  },
  scrollContent: {
    flex: 1,
    height: '100%',
    backgroundColor: '$backgroundSecondary'
  },
  container: {
    // margin: '$10',
    flexDirection: 'column',
    height: '100%'
  },
  back: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '$2'
  },
  backButton: {
    backgroundColor: '$hyperlink',
    borderRadius: 5,
    width: 20
  },
  header: {
    mt: '$5',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  accountInfo: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  kycInfoButtonWrap: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  account: {
    ml: '$5'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    mt: '$7',
    alignItems: 'center',
    height: 44
  },
  contentContainer: {
    mt: '$7'
  },
  containerWeb: {
    flex: 1,
    backgroundColor: 'transparent'
  },
  tagSx: { paddingX: '$2', paddingY: '$1', width: 'fit-content', borderRadius: 100 }
};
